import moment from 'moment/moment'
import styles from './timeBar.module.scss'
import { ComponentType, FunctionComponent, useState } from 'react'
import Desktop from '@/modules/desktopMobile/desktop'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import RateInfo from '../RateInfo/rateInfo'
import { RateSlot, RateType } from '@/types/StaffRate'
import ShowPresenceDialog from './Dialog/showPresenceDialog'

interface TimeBarProps {
    rateSlots: RateSlot[]
    opening: string
    closing: string
}

const TimeBar: FunctionComponent<TimeBarProps> = ({ rateSlots, opening, closing }: TimeBarProps) => {
    const totalMinutes: number = moment.duration(moment(closing, 'HH:mm').diff(moment(opening, 'HH:mm'))).asMinutes()
    const BAR_HEIGHT: number = 50

    const CustomTooltip: ComponentType<TooltipProps> = styled(({ className, ...props }: TooltipProps) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))(() => ({
        [`& .${tooltipClasses.arrow}`]: {
            color: 'rgba(0, 0, 0, 0.5)'
        },
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: '#FFFDF9',
            boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 5px 0px',
            padding: '20px',
            borderRadius: '20px',
            minWidth: '250px'
        }
    }))

    const [showPresenceDialog, setShowPresenceDialog] = useState<boolean>(false)
    const [selectedRateSlot, setSelectedRateSlot] = useState<RateSlot | null>(null)

    function handleSelectRateSlot(rateSlot: RateSlot): void {
        setSelectedRateSlot(rateSlot)
        setShowPresenceDialog(true)
    }

    function handleCloseDialog(): void {
        setShowPresenceDialog(false)
        setSelectedRateSlot(null)
    }

    return (
        <div className={styles.Container}>
            <div className={styles.OpeningClosing}>
                <p className={styles.Text}>{opening}</p>
                <p className={styles.Text}>{closing}</p>
            </div>
            <div className={styles.TimeBar} style={{ height: BAR_HEIGHT + 'px' }}>
                {rateSlots.map((rateSlot: RateSlot, index: number) => {
                    const startMinutes: number = rateSlot.start
                        .split(':')
                        .reduce((acc: number, time: string) => 60 * acc + parseInt(time, 10), 0)

                    const endMinutes: number = rateSlot.end
                        .split(':')
                        .reduce((acc: number, time: string) => 60 * acc + parseInt(time, 10), 0)

                    const width: number = ((endMinutes - startMinutes) / totalMinutes) * 100

                    const isFirst: boolean = index === 0

                    return (
                        <CustomTooltip
                            key={index}
                            placement={'top'}
                            TransitionProps={{ timeout: 0 }}
                            followCursor
                            title={
                                <RateInfo
                                    hours={rateSlot.start + ' - ' + rateSlot.end}
                                    hasRequiredStaff={rateSlot.hasRequiredStaff}
                                    staff={rateSlot.staffs.length}
                                    child={rateSlot.childs.length}
                                    available={rateSlot.available}
                                    realRate={rateSlot.realRate}
                                    potentialRate={rateSlot.potentialRate}
                                />
                            }
                            arrow
                            onClick={() => handleSelectRateSlot(rateSlot)}
                        >
                            <div
                                key={index}
                                className={`${styles.TimeSlot} 	${!isFirst && styles.BorderLeft}
								${
                                    rateSlot.type === RateType.success
                                        ? styles.success
                                        : rateSlot.type === RateType.warning
                                          ? styles.warning
                                          : styles.error
                                }`}
                                style={{ width: `${width}%` }}
                            >
                                <Desktop>
                                    {!isFirst && (
                                        <p
                                            className={styles.Text}
                                            style={{ transform: 'translateY(-25px) translateX(-25px)' }}
                                        >
                                            {rateSlot.start}
                                        </p>
                                    )}
                                </Desktop>
                            </div>
                        </CustomTooltip>
                    )
                })}
                {showPresenceDialog && selectedRateSlot && (
                    <ShowPresenceDialog
                        isDialogOpen={showPresenceDialog}
                        handleCloseDialog={handleCloseDialog}
                        start={selectedRateSlot.start}
                        end={selectedRateSlot.end}
                        childs={selectedRateSlot.childs}
                        staffs={selectedRateSlot.staffs}
                    />
                )}
            </div>
        </div>
    )
}

export default TimeBar
